import { Button, Collapse, Group, Paper, Select } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCaretDown, IconCaretRight } from "@tabler/icons-react";
import type { SupplierStatus } from "../../../../types/api.types";
import type { VersorgerstatusPeriod } from "../../../../utils/backend-types";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { luxonDateTimeToBackendDateOrDateTime } from "../../../../utils/dates/luxonDateTimeToBackendDateOrDateTime";
import { sortDateTimeOrNull } from "../../../../utils/dates/sortDateTimeOrNull";
import type {
  ChangeableFieldData,
  ChangeableFieldWidgetOptions
} from "../../../ChangeableFieldWidget/ChangeableFieldWidget";
import { ChangeableFieldWidget } from "../../../ChangeableFieldWidget/ChangeableFieldWidget";
import { SUPPLIER_STATUS_OPTIONS } from "../PersonForm.constants";
import { ChangeableSupplierStateHistory } from "./ChangeableSupplierStateHistory";
import { convertVersorgerstatusPeriodsToChangeableFieldData } from "./utils/convertVersorgerstatusPeriodsToChangeableFieldData";
import { getSupplierStatusLabel } from "./utils/getSupplierStatusLabel";

const changeableOptions = {
  addButtonText: "Versorgerstatus eintragen",
  saveButtonText: "Versorgerstatus übernehmen",
  endDatePlaceholder: "Aktuell",
  deleteTooltipText: "Versorgerstatus löschen",
  addNullStartDateWhenNoValues: true,
  canAddFutureDates: true
} satisfies ChangeableFieldWidgetOptions;

export interface ChangeableSupplierStateProps {
  versorgerstatusPeriods?: Array<VersorgerstatusPeriod>;
  onChange?: (periods: Array<VersorgerstatusPeriod>) => void;
}

function ChangeableSupplierState({
  versorgerstatusPeriods,
  onChange
}: ChangeableSupplierStateProps) {
  const [
    isHistoryCollapseOpen,
    { toggle: toggleHistory, close: closeHistory }
  ] = useDisclosure();
  const [isEditCollapseOpen, { open: openEdit, close: closeEdit }] =
    useDisclosure();

  const periods = versorgerstatusPeriods ?? [];
  const initialValues =
    convertVersorgerstatusPeriodsToChangeableFieldData(periods);

  const currentPeriod =
    periods
      .sort((a, b) =>
        sortDateTimeOrNull(
          backendDateOrDateTimeToLuxonDateTimeOrNull(a.firstDate),
          backendDateOrDateTimeToLuxonDateTimeOrNull(b.firstDate)
        )
      )
      .find((period) => period.lastDate === null) ?? periods.at(-1);
  const currentStatusLabel = currentPeriod
    ? getSupplierStatusLabel(currentPeriod.status)
    : "Kein Versorgerstatus hinterlegt";

  function handleClickHistoryButton() {
    closeEdit();
    toggleHistory();
  }

  function handleClickEditButton() {
    closeHistory();
    openEdit();
  }

  function handleSave(
    changeableFieldData: Array<ChangeableFieldData<SupplierStatus | null>>
  ) {
    const newPeriods = changeableFieldData
      .filter((data) => data.value !== null)
      .map<VersorgerstatusPeriod>((data) => ({
        status: data.value as SupplierStatus,
        firstDate: data.startDate
          ? luxonDateTimeToBackendDateOrDateTime(data.startDate)
          : null,
        lastDate: data.endDate
          ? luxonDateTimeToBackendDateOrDateTime(data.endDate)
          : null
      }));

    onChange?.(newPeriods);
    closeEdit();
    // Promise because ChangeableFieldWidget expects onDone to return a Promise.
    return Promise.resolve();
  }

  return (
    <div className="ChangeableSupplierState">
      <Group mb="xs">
        <Button
          flex={1}
          justify="flex-start"
          leftSection={
            isHistoryCollapseOpen ? (
              <IconCaretDown strokeWidth="1" />
            ) : (
              <IconCaretRight strokeWidth="1" />
            )
          }
          variant="default"
          onClick={handleClickHistoryButton}
        >
          {currentStatusLabel}
        </Button>
        <Button
          color="primary"
          disabled={isEditCollapseOpen}
          onClick={handleClickEditButton}
        >
          Versorgerstatus bearbeiten
        </Button>
      </Group>

      <Collapse expanded={isHistoryCollapseOpen}>
        {isHistoryCollapseOpen && (
          <ChangeableSupplierStateHistory versorgerstatusPeriods={periods} />
        )}
      </Collapse>

      <Collapse expanded={isEditCollapseOpen}>
        {isEditCollapseOpen && (
          <Paper>
            <ChangeableFieldWidget<SupplierStatus>
              dataName="Versorgerstatus"
              initialValues={initialValues}
              options={changeableOptions}
              valueColumns={[
                {
                  header: "Versorgerstatus*",
                  accessorKey: "value",
                  Cell: (props, onChangeValue) => (
                    <Select
                      data={SUPPLIER_STATUS_OPTIONS}
                      placeholder="Versorgerstatus auswählen"
                      value={props.cell.getValue<SupplierStatus | null>()}
                      onChange={(newValue) =>
                        onChangeValue(
                          props.row.index,
                          newValue as SupplierStatus
                        )
                      }
                    />
                  )
                }
              ]}
              onCancel={closeEdit}
              onDone={handleSave}
            />
          </Paper>
        )}
      </Collapse>
    </div>
  );
}

export { ChangeableSupplierState };
